import { Capability } from './capabilities';
import { sessionCan } from './capabilityMatrix';
import { RoleNames } from './roleNames';
import {
  BriqueNames,
  PortailNames,
  RoleSession,
  sessionHasBrique
} from './sessionContext';
import {
  ALL_ROLES,
  RoleVisibilityPolicy,
  resolvePolicy
} from './visibilityPolicy';

/**
 * Visibilité des items de menu (espace courtier) par rôle actif. Id = MenuItem.id.
 * Rôle absent = politique par défaut.
 */
const ROLE_MENU_POLICIES: Record<string, RoleVisibilityPolicy> = {
  [RoleNames.Courtier]: { allow: ALL_ROLES, deny: ['orgBrokers', 'leadsPartners'] },
  // Plateforme : les listes du portefeuille (contrats, devis, simulations,
  // clients) restent visibles même pour une compagnie (cf. DashboardMenu) ; on
  // exclut les items qui exigent un profil courtier individuel.
  // mesCourtiers : même route que « Courtiers de la plateforme » (orgBrokers) —
  // sous la casquette Plateforme on ne garde que l'entrée plateforme.
  [RoleNames.Plateforme]: { allow: ALL_ROLES, deny: ['myNotes', 'statistics', 'leadsPartners', 'mesCourtiers'] },
  // Seul rôle à voir les leads partenaires (et son unique écran métier).
  [RoleNames.GestionnaireLead]: { allow: ['leadsPartners'] }
};

const DEFAULT_MENU_POLICY: RoleVisibilityPolicy = {
  allow: ALL_ROLES,
  // leadsPartners : réservé au Gestionnaire Lead (deny par défaut).
  deny: ['orgBrokers', 'leadsPartners']
};

/** L'item de menu est-il visible pour ce rôle ? */
export const menuItemVisibleForRole = (
  roleName: string | null | undefined,
  itemId: string
): boolean => {
  if (!roleName) return false;
  const policy = resolvePolicy(ROLE_MENU_POLICIES, roleName, DEFAULT_MENU_POLICY);
  if (policy.deny?.includes(itemId)) return false;
  return policy.allow === ALL_ROLES || policy.allow.includes(itemId);
};

/**
 * L'item de menu est-il visible pour la SESSION ? Mode portail : le Portail
 * Plateforme reprend la politique du rôle Plateforme ; le Portail Distribution
 * reprend celle du Courtier, avec les leads partenaires ouverts par la brique
 * Gestion leads (plus une casquette séparée — c'est une capacité du portail) ;
 * le Portail Gestion n'affiche que le tableau de bord et les validations.
 */
export const menuItemVisibleForSession = (
  session: RoleSession,
  itemId: string
): boolean => {
  // Validations : la matrice centrale (brique Valideur) décide, quel que soit
  // le portail, et avant le repli legacy : la seule permission ABP laisserait
  // l'entrée à un admin.
  if (itemId === 'validations') {
    return sessionCan(session, Capability.ValidationsAccess);
  }

  if (session.mode === 'legacy') {
    return menuItemVisibleForRole(session.roleName, itemId);
  }

  // Habilitations tunnels : règle portée par la matrice centrale (brique
  // Conception d'offre requise), quel que soit le portail actif — la gouvernance
  // seule ne suffit pas.
  if (itemId === 'habilitationsTunnels') {
    return sessionCan(session, Capability.TunnelHabilitationsAccess);
  }

  if (session.portail === PortailNames.Plateforme) {
    return menuItemVisibleForRole(RoleNames.Plateforme, itemId);
  }

  if (session.portail === PortailNames.Distribution) {
    if (itemId === 'leadsPartners') {
      return sessionHasBrique(session, BriqueNames.GestionLeads);
    }

    return menuItemVisibleForRole(RoleNames.Courtier, itemId);
  }

  // Portail Gestion (minimal) : tableau de bord et validations uniquement
  // (les validations sont déjà tranchées plus haut par la matrice).
  if (session.portail === PortailNames.Gestion) {
    return itemId === 'dashboard';
  }

  return false;
};
